"use client";

import React from "react";
import { format, addMinutes } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../dialog";
import { Button } from "../../button";
import { Calendar, CheckCircle } from "lucide-react";
import { MeetingFormData } from "../types/meeting";
import { parseFormDateTime } from "../utils/meetingFormUtils";
import { getPlatformLabel } from "../constants/meetingConstants";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  formData: MeetingFormData;
  currentAttendees: string[];
  isSubmitting: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  formData,
  currentAttendees,
  isSubmitting,
}) => {
  const startDateTime = parseFormDateTime(formData.startDate, formData.startTime);
  const endDateTime = addMinutes(startDateTime, formData.duration);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-card border border-gray-200 shadow-xl">
        <DialogHeader className="pb-6">
          <DialogTitle className="flex items-center gap-3 text-2xl font-semibold text-slate-900">
            <div className="p-2 bg-blue-50 rounded-lg">
              <CheckCircle className="h-6 w-6 text-blue-600" />
            </div>
            Confirm Meeting Details
          </DialogTitle>
          <DialogDescription className="text-slate-600 text-base">
            Please review your meeting details before creating the event.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="p-4 bg-card rounded-lg border border-gray-200 shadow-sm">
            <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Meeting Summary
            </h4>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Title:</span>
                <span className="text-card-foreground">{formData.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Date:</span>
                <span className="text-card-foreground">
                  {format(startDateTime, "PPP")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Time:</span>
                <span className="text-card-foreground">
                  {format(startDateTime, "p")} - {format(endDateTime, "p")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Duration:</span>
                <span className="text-card-foreground">
                  {formData.duration} minutes
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Platform:</span>
                <span className="text-card-foreground">
                  {getPlatformLabel(formData.platform)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Type:</span>
                <span className="text-card-foreground">
                  {formData.isOnline ? "Online Meeting" : "In-Person"}
                </span>
              </div>
              {!formData.isOnline && formData.location && (
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">Location:</span>
                  <span className="text-card-foreground">
                    {formData.location}
                  </span>
                </div>
              )}
              {currentAttendees.length > 0 && (
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">Attendees:</span>
                    <span className="text-card-foreground">
                      {currentAttendees.length} people
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    {currentAttendees.join(", ")}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 border-gray-300 text-slate-700 hover:bg-card-50 hover:border-gray-400 transition-colors"
            >
              Back to Edit
            </Button>
            <Button
              onClick={onConfirm}
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-colors"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Create Meeting
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
